import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VG_ORANGE } from "../lib/brand";
import { resetPassword } from "../lib/adminAuth";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/admin"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This reset link is invalid or has expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-password-page">
      <PageHero title="Reset Password" subtitle="Choose a new password for your inquiry-inbox login." testId="reset-hero" />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-xl border border-neutral-200 bg-white p-8 shadow-sm">
          {!token ? (
            <p className="text-neutral-600" data-testid="reset-invalid">
              This reset link is missing its token. Please use the link from your email, or{" "}
              <Link to="/admin" className="font-semibold" style={{ color: VG_ORANGE }}>request a new one</Link>.
            </p>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
              <h3 className="mt-4 font-heading text-xl font-bold text-neutral-900">Password updated</h3>
              <p className="mt-2 text-sm text-neutral-600">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ backgroundColor: VG_ORANGE }}>
                  <Lock className="h-5 w-5" />
                </span>
                <h1 className="font-heading text-xl font-bold text-neutral-900">New Password</h1>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="rp-pass">New password</Label>
                  <Input id="rp-pass" data-testid="reset-password-input" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rp-confirm">Confirm password</Label>
                  <Input id="rp-confirm" data-testid="reset-confirm-input" type="password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="reset-submit"
                  className="w-full text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }}>
                  {submitting ? "Updating..." : "Update Password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
